import { z } from 'zod';

const fields = z
  .object({
    receivingTargetCents: z.number().int().positive(),
    inventoryCeilingCents: z.number().int().positive(),
    expectedFillRate: z.number().positive().max(1),
    firstBuyCapCents: z.number().int().positive(),
    fixedFreightCents: z.number().int().nonnegative(),
    minimumOrderGoodsCents: z.number().int().positive(),
    /** Per-unit prep cost at planning time (2026-09-05 wrap-prep design) —
     *  replaces the flat feeProfile.defaultPrepPerUnit rate for the planning
     *  path only. prepWrappedPerUnitCents applies when listing_map.prep_requires_wrap
     *  is true, prepPerUnitCents otherwise. */
    prepPerUnitCents: z.number().int().nonnegative(),
    prepWrappedPerUnitCents: z.number().int().nonnegative(),
    /** Sourcing pre-screen (Faraz 2026-09-06 catalogue-screen design): which
     *  websiteDisplayGroup sales-rank key a screened candidate's rank is
     *  compared against, and the cutoff above which it is dropped as
     *  rank_above_cutoff before any research is spent on it. */
    sourcingRankKey: z.string().min(1),
    /** 150,000 sits above every genuine mover we measured in Grocery
     *  (#21,731 / #28,528 / #128,264) and below the item that triggered this
     *  work — one we own the Buy Box on and still cannot sell, at #244,338. */
    sourcingMaxSalesRank: z.number().int().positive(),
  })
  .strict();

export const BuyingSettings = fields.superRefine((settings, ctx) => {
  const orderedBudget = Math.ceil(settings.receivingTargetCents / settings.expectedFillRate);
  for (const field of ['firstBuyCapCents', 'minimumOrderGoodsCents'] as const) {
    if (settings[field] > orderedBudget) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: 'Must not exceed the gross ordered budget',
      });
    }
  }
});
export type BuyingSettings = z.infer<typeof BuyingSettings>;
export const UpdateBuyingSettingsRequest = fields.partial();
export const DEFAULT_BUYING_SETTINGS: BuyingSettings = {
  receivingTargetCents: 200000,
  inventoryCeilingCents: 2000000,
  expectedFillRate: 0.775,
  firstBuyCapCents: 20000,
  fixedFreightCents: 6000,
  minimumOrderGoodsCents: 150000,
  prepPerUnitCents: 100,
  prepWrappedPerUnitCents: 150,
  sourcingRankKey: 'grocery_display_on_website',
  sourcingMaxSalesRank: 150000,
};
